import React, { Fragment, memo, useCallback, useEffect } from 'react'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchContractorFiles } from 'src/constants/actionTypes'
import { ContractorFile } from 'src/interfaces/file'
import { ContractorFileItem } from '../contractor-files/ContractorFileItem'
import { EmptyFiles } from '../contractor-files/EmptyFiles'
import { CreateContractorFileButton } from '../contractor-files/CreateContractorFileButton'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface FilesTabProps {
  contractorId: string
  onUpdate(): void | Promise<void>
}

const FilesTabComponent = ({ contractorId, onUpdate }: FilesTabProps) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [contractorFiles, setContractorFiles] = React.useState<Array<ContractorFile>>([])
  const [isLoaded, setIsLoaded] = React.useState(false)

  const fetchContractorFiles = useCallback(async () => {
    try {
      const files: Array<ContractorFile> = await createAsyncAction(
        dispatch,
        FetchContractorFiles.request({ contractorId }),
      )

      setContractorFiles(files)
    } catch {}

    setIsLoaded(true)
  }, [dispatch, contractorId])

  useEffect(() => {
    fetchContractorFiles()
  }, [fetchContractorFiles])

  const handleContractorFilesRefresh = useCallback(async () => {
    await fetchContractorFiles()
    await onUpdate()
  }, [fetchContractorFiles, onUpdate])

  return isLoaded ? (
    <div css={tw`flex flex-1 flex-col overflow-y-auto h-full bg-[rgba(246, 246, 246, 1)]`}>
      <div css={tw`p-4 pb-20`}>
        {contractorFiles.length ? (
          <Fragment>
            {hasPermission(UserPermission.ContractorFilesCreate) && (
              <div css={tw`mb-4`}>
                <CreateContractorFileButton
                  contractorId={contractorId}
                  onUpdate={handleContractorFilesRefresh}
                />
              </div>
            )}
            {contractorFiles.map((file) => (
              <ContractorFileItem
                contractorFile={file}
                contractorId={contractorId}
                refreshContractorFiles={handleContractorFilesRefresh}
              />
            ))}
          </Fragment>
        ) : (
          <EmptyFiles contractorId={contractorId} onUpdate={handleContractorFilesRefresh} />
        )}
      </div>
    </div>
  ) : null
}

export const FilesTab = memo(FilesTabComponent)
